import { Box, Chip, Stack, Typography } from "@mui/material";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { appImages } from "../../assets";
import { useSnackbar } from "../../lib/context/snackbarContext";
import {
  getProductDetailsByUrl,
  getRelatedProducts,
} from "../../services/productsFn";
import AppFooter from "../footer";
import NavBar from "../NavBar";
import ProductDetailWithTabsSkeleton from "../skeletons/productDetailSkeleton";
import Detail from "./Detail";
import InfoTabs from "./InfoTabs";
import RelatedProducts from "./RelatedProducts";

const ProductDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);
  const [productDetails, setProductDetails] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState();
  const [detailError, setDetailError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (params.url) {
      fetchProductAndRelated(params.url);
    }
  }, [params.url]);

  const fetchProductAndRelated = async (url) => {
    try {
      setIsLoading(true);

      const [detailsResponse, relatedResponse] = await Promise.all([
        getProductDetailsByUrl(url),
        getRelatedProducts(url),
      ]);

      // Handle product details
      if (detailsResponse?.data.status === "success") {
        const data = detailsResponse.data.data;

        const galleryImages = data.images?.map((image) => ({
          original: image,
          thumbnail: image,
        }));

        setProductDetails({ ...data, galleryImages });
      } else {
        setDetailError(true);
        showSnackbar(
          detailsResponse?.data?.message || t("errorFetchingDetails"),
          "error"
        );
      }

      if (relatedResponse?.data.status === "success") {
        setRelatedProducts(relatedResponse.data.data);
      } else {
        showSnackbar(
          relatedResponse?.data?.message || t("errorFetchingRelatedProducts"),
          "error"
        );
      }
    } catch (error) {
      setDetailError(true);
      showSnackbar(error?.message || t("anErrorOccurred"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <NavBar />

      {isLoading ? (
        <Box margintop={10} mt={18}>
          <ProductDetailWithTabsSkeleton />
        </Box>
      ) : (
        <>
          {/* Hero Section */}
          <Box mt={16} mb={4}>
            <Box
              height="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                variant="heading_pr"
                component="h1"
                textAlign="center"
                lineHeight="normal"
                maxWidth={600}
                margin="auto"
              >
                {t("productDetailHead")}.
              </Typography>
            </Box>
          </Box>

          {/* Main Content Section */}
          <Box
            sx={{
              px: 3,
              py: 5,
              backgroundColor: "text.white",
              borderRadius: 3,
              position: "relative",
              top: "-10px",
              maxWidth: "1300px",
              margin: "auto",
            }}
          >
            {/* Error Handling */}
            {detailError && (
              <Stack alignItems="center">
                <Box
                  component="img"
                  src={appImages.noProductFoundImg}
                  width={{ xs: "80%", md: "50%" }}
                />
              </Stack>
            )}

            {/* Product Details and Info s */}
            {!detailError && (
              <>
                <Detail productDetail={productDetails} />
                <InfoTabs productDetail={productDetails} />
              </>
            )}

            {/* Related Products */}
            {relatedProducts?.length > 0 && (
              <RelatedProducts products={relatedProducts} />
            )}
          </Box>

          <AppFooter bgcolor />
        </>
      )}
      <Chip
        label={t("goBack")}
        onClick={() => navigate(-1)}
        sx={{
          position: "fixed",
          bottom: 15,
          left: 10,
          zIndex: 100000,
          color: "common.white",
          backgroundColor: "primary.main",
          "&:hover": {
            backgroundColor: "primary.main",
          },
        }}
      />
    </>
  );
};

export default ProductDetail;
